import React, { FC, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import dateFns from "date-fns";

import SelectBox from "../../../../../components/select-box/select-box";
import LeftInfoModal from "../../../../../components/left-info-modal";
import Loader from "../../../../../components/loader";
import { NewInsuredContainer } from "../../new-insured/newInsuredStepper-style";
import { images } from "../../../../../assets";

import ConstantsServices from "../../../../../services/ConstantsServices";
import { MASCOTA_TYPE, PERSONA_TYPE, SEND_TO } from "../../../../../constants/supplements";
import { SupplementsContext } from "../../../../../context/supplements/SupplementsContext";
import PoliciesServices from "../../../../../services/PoliciesServices";
import { getNifTomador, getPolicyData, getPolicyNumber, getSubramo } from "../../utils";
import { defaultCheck, mergeInsurancesAndTaker, mergeOnlyInsureds } from "../../basic-data/utils";
import { deepGet } from "../../../../../utils/utility";

import {
  RiskDetailPersonDataDto,
  RiskDetailPetDataDto,
} from "../../../../clients/clients-policies/dto/detail-clients-policies-layout-dto";
import { ResultMergeInsurancesAndTaker, Taker } from "../../basic-data/basic-data.types";
import { SelectProps } from "./select-policy-holder.types";
import WASISAServices from "../../../../../services/WASISAServices";

const SelectPolicyHolder: FC<RouteComponentProps & SelectProps> = ({
  history,
  handleChange,
  handleMultipleChanges,
  noFormWidth = false,
  isFirstTime,
  setIsFirstTime,
  values,
  title,
  onlyInsureds,
}) => {
  const [personsList, setPersonsList] = useState<ResultMergeInsurancesAndTaker[]>([]);
  const [getTakerFinished, setGetTakerFinished] = useState<boolean>(false);
  const [getInsurancesFinished, setGetInsurancesFinished] = useState<boolean>(false);
  const [insurances, setInsurances] = useState<RiskDetailPersonDataDto[]>([]);
  const [pets, setPets] = useState<RiskDetailPetDataDto[]>([]);
  const [modalError, setModalError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [petBreedList, setPetBreedList] = useState<{ label: string, value: number }[]>([]);

  const supplementsContext = React.useContext(SupplementsContext);

  useEffect(() => {
    getData();
    mergeData();
  }, []);

  useEffect(() => {
    getTakerFinished &&
      getInsurancesFinished &&
      supplementsContext.taker &&
      insurances &&
      mergeData();
  }, [getTakerFinished, getInsurancesFinished]);

  const getData = async () => {
    try {
      setLoading(true);
      const takerDetailResponse = await PoliciesServices.searchTakerDetail(
        () => {},
        false,
        { codMediador: getPolicyData().mediator, nifTomador: getNifTomador() }
      );
      const takerDetailData =
        deepGet(takerDetailResponse, "takerDetail", []) || [];
      const takerDetail = takerDetailData ? takerDetailData[0] : {};

      supplementsContext.setTaker(takerDetail);
      setGetTakerFinished(true);

      if (!modalError) {
        const riskDetail = await PoliciesServices.searchRiskDetail(
          () => {},
          false,
          [],
          {
            subramo: getSubramo(),
            codMediador: getPolicyData().mediator,
            searchType: "Poliza",
            data: getPolicyNumber(),
          }
        );
        setInsurances(deepGet(riskDetail, "riskDetail.personList", []) || []);
        setPets(deepGet(riskDetail, "riskDetail.petList", []) || []);
        setGetInsurancesFinished(true);
      }
    } catch {
      setModalError(true);
    } finally {
      setLoading(false);
    }
  };

  const mergeData = () => {
    if (onlyInsureds) {
      setPersonsList(mergeOnlyInsureds(insurances, pets)); 
    } else {
      setPersonsList(mergeInsurancesAndTaker(insurances, supplementsContext.taker as Taker, pets, handleMultipleChanges));
    }
  };

  const getPetBreedCode = async (petType: string, breed: string) => {
    let breeds = petBreedList;

    if (breeds.length === 0) {
      breeds = await WASISAServices.getPetBreeds({
        loader: true,
        setLoading: () => {},
        params: [`petType=${petType}`]
      });
      setPetBreedList(breeds);
    }

    const breedCode = breeds.find((pet) => pet.label === breed);
    return (breedCode && breedCode.value) || "";
  };

  const onSelectInsurance = async (value: string) => {
    handleChange("nameSelected", value);
    const selectedPerson = personsList && personsList[Number(value)];
    if (selectedPerson) {
      const birthDate: string = selectedPerson.birthDate || "";
      const formattedBirthDate = dateFns.format(birthDate, "DD/MM/YYYY", {
        locale: "es",
      });
      const personData = {
        docType: selectedPerson.docType || "",
        docNumber: selectedPerson.nif || "",
        name: selectedPerson.name || "",
        firstName: selectedPerson.firstName || "",
        lastname1: selectedPerson.lastname1 || "",
        lastname2: selectedPerson.lastname2 || "",
        gender: selectedPerson.gender || "",
        oldProvince: selectedPerson.oldProvince || "",
        oldTown: selectedPerson.oldTown || "",
        oldAddress: selectedPerson.oldAddress || "",
        orden: selectedPerson.orden || "",
        birthDate:
          formattedBirthDate !== "Invalid Date" ? formattedBirthDate : birthDate.substring(0, 10),
        numChip: "",
        petType: "",
        breed: "",
        petTypeCode: "",
        petBreedCode: "",
      };

      const isPet = selectedPerson.label.includes(PERSONA_TYPE.MASCOTA);
      if (isPet) {
        const petType = selectedPerson.petType || "";
        const breed = selectedPerson.breed || "";
        const petTypeCode = petType ? MASCOTA_TYPE[petType.toUpperCase()] : "";
        const petBreedCode = await getPetBreedCode(petType, breed);

        personData.numChip = selectedPerson.numChip || "";
        personData.breed = breed;
        personData.petType = petType;
        personData.petTypeCode = petTypeCode;
        personData.petBreedCode = `${petBreedCode}`;

        supplementsContext.setSendTo(SEND_TO.SENDA_API);
      }
      handleMultipleChanges && handleMultipleChanges(personData);
    }
    if (value === "0") {
      handleChange("type", PERSONA_TYPE.TOMADOR, "");
    } else {
      if (personsList) {
        const person = personsList.find((person) => person.value === value);
        if (person && person.label) {
          handleChange("type", person.label.split(" - ")[0] || "", "");
        }
      }
    }
    if (!isFirstTime) {
      handleChange("check", defaultCheck);
      supplementsContext.setSupplementChildIds([]);
    } else if (setIsFirstTime) {
      setIsFirstTime(false);
    }
  };

  const redirect = () => {
    setModalError(false);
    history.push(`/suplementos/${getPolicyNumber()}`);
  };

  return (
    <NewInsuredContainer>
      <div className={`personal-data-row  container-background ${noFormWidth ? "" : "form-width"}`}>
        <div className="personal-data-item col-100">
          <div className="selectBox">
            <SelectBox
              required={true}
              optionsText={personsList || []}
              optionKey={"value"}
              optionValue={"label"}
              initialValue={values.nameSelected ? values.nameSelected : undefined}
              defaultValue={values.nameSelected ? values.nameSelected : undefined}
              disabled={false}
              onChange={onSelectInsurance}
              placeholder={title}
            />
            <label> {title} </label>
          </div>
        </div>
      </div>
      {loading && <Loader />}
      <LeftInfoModal
        img={images.WarningError}
        mainTitle={"error"}
        mainText={"Ha habido un error al cargar datos de los asegurados"}
        noSecondaryButton={true}
        buttonText="Volver"
        active={modalError}
        onClickAccept={redirect}
        close={redirect}
        className={"error"}
      />
    </NewInsuredContainer>
  );
};

export default withRouter(SelectPolicyHolder);
